// ============================================================================
// CartItemsTab — Vendor Agent cart items lifecycle view
//
// Shows one row per CartItemDetails that has at least one VendorAgentPrediction,
// with a horizontal 3-step progress tracker:
//   1. Availability Requested  — MD sent a message about this cart_item
//   2. Vendor Messaged         — a VendorAgentPrediction row exists
//   3. Lead Time Filled        — CartItemDetails.lead_time IS NOT NULL (color chip)
//
// Consumes GET /vendor-agent-dashboard/cart-items/ in
// ai_agents/vendor_agent/views.py::vendor_agent_dashboard_cart_items
// ============================================================================
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  InboxOutlined,
  ReloadOutlined,
  SearchOutlined,
  WarningFilled,
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Input,
  Pagination,
  Select,
  Spin,
  Tooltip,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getVendorAgentCartItems } from "../../apis/vendor_agent_dashboard.api";
import {
  ICartItemLifecycleRow,
  ICartItemStepFilter,
} from "../../interfaces/vendor_agent_dashboard.interface";
import styles from "./cart_items_tab.module.scss";

const { RangePicker } = DatePicker;

// ──────────────── helpers ────────────────

const formatRelativeTime = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = dayjs(iso);
  const now = dayjs();
  const diffMin = now.diff(d, "minute");
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = now.diff(d, "hour");
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = now.diff(d, "day");
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.format("DD MMM");
};

type LeadTimeColor = "green" | "blue" | "yellow" | "red" | "grey";

const resolveLeadTimeColor = (row: ICartItemLifecycleRow): LeadTimeColor => {
  const { lead_time, cart_quantity, latest_prediction } = row;
  if (lead_time == null) return "grey";
  const predQty = latest_prediction?.predicted_quantity ?? null;
  const isAvail = latest_prediction?.is_available ?? null;
  // Not available at all
  if (isAvail === false) return "red";
  // Severely under-quoted quantity
  if (predQty != null && cart_quantity > 0 && predQty < cart_quantity * 0.5) {
    return "red";
  }
  // In stock / ready
  if (lead_time === 0) return "green";
  // Short lead time with full quantity available
  if (lead_time <= 3 && (predQty == null || predQty >= cart_quantity)) {
    return "blue";
  }
  // Longer lead time OR under-quoted qty
  if (lead_time > 3 || (predQty != null && predQty < cart_quantity)) {
    return "yellow";
  }
  return "grey";
};

const chipClassMap: Record<LeadTimeColor, string> = {
  green: styles.chipGreen,
  blue: styles.chipBlue,
  yellow: styles.chipYellow,
  red: styles.chipRed,
  grey: styles.chipGrey,
};

const leadTimeChipText = (
  row: ICartItemLifecycleRow,
  color: LeadTimeColor
): string => {
  if (row.lead_time == null) return "Lead time pending";
  if (row.lead_time === 0) return "In stock (0d)";
  const base = `${row.lead_time} day${row.lead_time === 1 ? "" : "s"}`;
  if (color === "red") {
    if (row.latest_prediction?.is_available === false) {
      return `${base} · not available`;
    }
    return `${base} · qty short`;
  }
  if (color === "yellow") {
    const predQty = row.latest_prediction?.predicted_quantity ?? null;
    if (predQty != null && predQty < row.cart_quantity) {
      return `${base} · only ${predQty}/${row.cart_quantity}`;
    }
    return base;
  }
  return base;
};

// ──────────────── stepper ────────────────

interface StepConfig {
  key: "availability_requested" | "vendor_messaged" | "lead_time_filled";
  label: string;
  subtitle: (row: ICartItemLifecycleRow) => string;
}

const STEPS: StepConfig[] = [
  {
    key: "availability_requested",
    label: "Availability Requested",
    subtitle: (r) =>
      r.steps.availability_requested ? "MD reached out" : "not yet",
  },
  {
    key: "vendor_messaged",
    label: "Vendor Messaged",
    subtitle: (r) =>
      r.latest_prediction
        ? `${r.prediction_count} reply${r.prediction_count === 1 ? "" : "s"}`
        : "—",
  },
  {
    key: "lead_time_filled",
    label: "Lead Time Filled",
    subtitle: (r) =>
      r.lead_time != null
        ? `set ${formatRelativeTime(r.lead_time_updated_at)}`
        : "pending",
  },
];

const StepperRow: React.FC<{ row: ICartItemLifecycleRow }> = ({ row }) => {
  return (
    <div className={styles.stepper}>
      {STEPS.map((step, idx) => {
        const done = row.steps[step.key];
        const beadClass = `${styles.stepBead} ${
          done ? styles.stepBeadDone : styles.stepBeadPending
        }`;
        return (
          <React.Fragment key={step.key}>
            <div className={styles.stepItem}>
              <div className={beadClass}>
                {done ? (
                  <CheckCircleFilled />
                ) : (
                  <ClockCircleOutlined />
                )}
              </div>
              <div className={styles.stepTextWrap}>
                <div
                  className={`${styles.stepLabel} ${
                    done ? styles.stepLabelDone : styles.stepLabelPending
                  }`}
                >
                  {step.label}
                </div>
                <div className={styles.stepSub}>{step.subtitle(row)}</div>
              </div>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={`${styles.stepConnector} ${
                  done && row.steps[STEPS[idx + 1].key]
                    ? styles.stepConnectorDone
                    : done
                    ? styles.stepConnectorHalf
                    : styles.stepConnectorPending
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ──────────────── row card ────────────────

const LifecycleRowCard: React.FC<{
  row: ICartItemLifecycleRow;
  onOpen: () => void;
}> = ({ row, onOpen }) => {
  const color = resolveLeadTimeColor(row);
  const chipLabel = leadTimeChipText(row, color);
  const vendorName = row.latest_prediction?.vendor?.name || "—";
  const cartQtyLabel = `${row.cart_quantity}`;

  return (
    <div
      className={styles.rowCard}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen();
      }}
    >
      <div className={styles.rowHeader}>
        <div className={styles.rowTitleWrap}>
          <span className={styles.cartNumber}>
            {row.cart_number || `CI-${row.cart_item_id}`}
          </span>
          <span className={styles.variantName} title={row.variant_name}>
            {row.variant_name || "Unnamed product"}
          </span>
          <span className={styles.qtyPill}>Qty {cartQtyLabel}</span>
        </div>
        <div className={styles.rowRight}>
          <span className={styles.vendorName}>{vendorName}</span>
          <span className={`${styles.leadTimeChip} ${chipClassMap[color]}`}>
            {color === "red" && <WarningFilled />}
            {chipLabel}
          </span>
        </div>
      </div>

      <StepperRow row={row} />

      {row.latest_prediction?.message_body_preview ? (
        <div className={styles.messagePreview}>
          "{row.latest_prediction.message_body_preview}"
        </div>
      ) : null}
    </div>
  );
};

// ──────────────── main tab component ────────────────

interface CartItemsTabProps {
  /** Optional: open the existing DetailDrawer for the clicked row's latest prediction. */
  onOpenPredictionDetail?: (predictionId: number) => void;
}

const PAGE_SIZE = 15;

const CartItemsTab: React.FC<CartItemsTabProps> = ({
  onOpenPredictionDetail,
}) => {
  const [rows, setRows] = useState<ICartItemLifecycleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState("");
  const [stepFilter, setStepFilter] = useState<ICartItemStepFilter | undefined>(
    undefined
  );
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await getVendorAgentCartItems({
      page,
      page_size: PAGE_SIZE,
      search: search || undefined,
      step: stepFilter,
      since: dateRange?.[0]?.startOf("day").toISOString(),
      until: dateRange?.[1]?.endOf("day").toISOString(),
    });
    if (res?.success) {
      setRows(res.results);
      setTotal(res.total);
    } else {
      setRows([]);
      setTotal(0);
    }
    setLoading(false);
  }, [page, search, stepFilter, dateRange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleClear = () => {
    setSearch("");
    setStepFilter(undefined);
    setDateRange(null);
    setPage(1);
  };

  const handleRowClick = (row: ICartItemLifecycleRow) => {
    if (row.latest_prediction && onOpenPredictionDetail) {
      onOpenPredictionDetail(row.latest_prediction.id);
    }
  };

  // Local summary counts for the tiny header above filters
  const summary = useMemo(() => {
    let awaitingReq = 0;
    let awaitingLt = 0;
    let completed = 0;
    for (const r of rows) {
      if (!r.steps.availability_requested) awaitingReq++;
      if (!r.steps.lead_time_filled) awaitingLt++;
      if (r.steps.lead_time_filled) completed++;
    }
    return { awaitingReq, awaitingLt, completed };
  }, [rows]);

  return (
    <div className={styles.container}>
      {/* ===== Filter bar ===== */}
      <div className={styles.filterBar}>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Search</span>
          <Input
            allowClear
            placeholder="Cart number, product…"
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Stage</span>
          <Select
            allowClear
            placeholder="Any stage"
            value={stepFilter}
            onChange={(v) => {
              setStepFilter(v);
              setPage(1);
            }}
            options={[
              { value: "awaiting_request", label: "Awaiting MD Request" },
              { value: "awaiting_lead_time", label: "Awaiting Lead Time" },
              { value: "completed", label: "Completed" },
            ]}
          />
        </div>

        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Date Range</span>
          <RangePicker
            value={dateRange as any}
            onChange={(v) => {
              setDateRange(v as [Dayjs, Dayjs] | null);
              setPage(1);
            }}
          />
        </div>

        <Button onClick={handleClear} className={styles.clearBtn}>
          Clear
        </Button>

        <Tooltip title="Refresh">
          <Button
            icon={<ReloadOutlined />}
            onClick={load}
            loading={loading}
            className={styles.clearBtn}
          >
            Refresh
          </Button>
        </Tooltip>
      </div>

      {/* ===== Quick summary of current page ===== */}
      {!loading && rows.length > 0 && (
        <div className={styles.pageSummary}>
          <span>
            Showing {rows.length} of {total} items
          </span>
          <span className={styles.pageSummaryDot}>•</span>
          <span>
            {summary.completed} completed · {summary.awaitingLt} awaiting lead
            time · {summary.awaitingReq} awaiting request
          </span>
        </div>
      )}

      {/* ===== Rows ===== */}
      {loading ? (
        <div className={styles.centerWrap}>
          <Spin size="large" />
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.centerWrap}>
          <InboxOutlined className={styles.emptyIcon} />
          <h3 className={styles.emptyTitle}>No cart items with agent activity</h3>
          <p className={styles.emptySubtitle}>
            Cart items will appear here once the agent requests availability
            from vendors and receives replies.
          </p>
        </div>
      ) : (
        <>
          <div className={styles.rowsList}>
            {rows.map((row) => (
              <LifecycleRowCard
                key={row.cart_item_id}
                row={row}
                onOpen={() => handleRowClick(row)}
              />
            ))}
          </div>

          <div className={styles.paginationRow}>
            <Pagination
              current={page}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={(p) => setPage(p)}
              showSizeChanger={false}
              showTotal={(t, [from, to]) => `${from}-${to} of ${t}`}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CartItemsTab;
